import React from 'react';
import { ChatMessage as ChatMessageType } from '../../store/chatStore';
import ActivityLog from './ActivityLog';

interface ChatMessageProps {
  message: ChatMessageType;
}

/** Render markdown-like content for the TUI style */
function renderContent(text: string): React.ReactNode[] {
  const elements: React.ReactNode[] = [];

  // Split into code blocks and non-code blocks
  const codeBlockRegex = /```(\w*)\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;
  let blockKey = 0;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    // Render text before code block
    if (match.index > lastIndex) {
      elements.push(...renderTextLines(text.slice(lastIndex, match.index), blockKey));
    }
    // Render code block
    const lang = match[1];
    const code = match[2];
    elements.push(
      <div key={`code-${blockKey++}`} className="my-2 rounded border border-neutral-800 bg-neutral-900/80 overflow-x-auto">
        {lang && (
          <div className="text-[10px] text-neutral-600 px-3 py-1 border-b border-neutral-800">{lang}</div>
        )}
        <pre className="text-xs leading-relaxed p-3 text-neutral-300">
          <code>{code}</code>
        </pre>
      </div>
    );
    lastIndex = match.index + match[0].length;
  }

  // Render remaining text
  if (lastIndex < text.length) {
    elements.push(...renderTextLines(text.slice(lastIndex), blockKey));
  }

  return elements;
}

function renderTextLines(text: string, keyOffset: number): React.ReactNode[] {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  lines.forEach((line, i) => {
    const key = `${keyOffset}-${i}`;

    // Empty line = spacing
    if (line.trim() === '') {
      elements.push(<div key={key} className="h-1.5" />);
      return;
    }

    // Headers
    const headerMatch = line.match(/^(#{1,3})\s+(.*)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const headerText = renderInline(headerMatch[2], key);
      const cls = level === 1
        ? 'text-sm font-bold text-neutral-100 mt-2'
        : level === 2
        ? 'text-sm font-semibold text-neutral-200 mt-1.5'
        : 'text-xs font-semibold text-neutral-300 mt-1';
      elements.push(<div key={key} className={cls}>{headerText}</div>);
      return;
    }

    // Bullet list
    const bulletMatch = line.match(/^(\s*)[-*]\s+(.*)$/);
    if (bulletMatch) {
      const indent = bulletMatch[1].length > 0;
      elements.push(
        <div key={key} className={`flex items-start gap-1.5 ${indent ? 'ml-4' : ''}`}>
          <span className="text-neutral-600 shrink-0">-</span>
          <span>{renderInline(bulletMatch[2], key)}</span>
        </div>
      );
      return;
    }

    // Numbered list
    const numMatch = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
    if (numMatch) {
      const indent = numMatch[1].length > 0;
      elements.push(
        <div key={key} className={`flex items-start gap-1.5 ${indent ? 'ml-4' : ''}`}>
          <span className="text-neutral-600 shrink-0">{numMatch[2]}.</span>
          <span>{renderInline(numMatch[3], key)}</span>
        </div>
      );
      return;
    }

    // Table row (pipe-separated)
    if (line.includes('|') && line.trim().startsWith('|')) {
      // Skip separator rows like |---|---|
      if (/^\|[\s-|]+\|$/.test(line.trim())) {
        elements.push(<div key={key} className="border-b border-neutral-800 my-0.5" />);
        return;
      }
      const cells = line.split('|').filter(c => c.trim() !== '');
      elements.push(
        <div key={key} className="flex gap-3 text-xs">
          {cells.map((cell, ci) => (
            <span key={ci} className={ci === 0 ? 'text-neutral-300 min-w-[100px]' : 'text-neutral-400'}>
              {renderInline(cell.trim(), `${key}-c${ci}`)}
            </span>
          ))}
        </div>
      );
      return;
    }

    // Regular line
    elements.push(
      <div key={key}>{renderInline(line, key)}</div>
    );
  });

  return elements;
}

/** Render inline formatting: **bold**, `code`, links */
function renderInline(text: string, keyPrefix: string | number): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Match **bold**, `code`, and [text](url)
  const inlineRegex = /(\*\*(.+?)\*\*)|(`([^`]+?)`)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let lastIndex = 0;
  let match;
  let partKey = 0;

  while ((match = inlineRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1]) {
      // **bold**
      parts.push(
        <strong key={`${keyPrefix}-b${partKey++}`} className="text-neutral-100 font-semibold">
          {match[2]}
        </strong>
      );
    } else if (match[3]) {
      // `inline code`
      parts.push(
        <code key={`${keyPrefix}-c${partKey++}`} className="bg-neutral-800 text-neutral-300 px-1 rounded text-xs">
          {match[4]}
        </code>
      );
    } else if (match[5]) {
      // [link](url)
      parts.push(
        <a key={`${keyPrefix}-a${partKey++}`} href={match[7]} className="text-blue-400 underline" target="_blank" rel="noopener noreferrer">
          {match[6]}
        </a>
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  if (parts.length === 0) parts.push(text);
  return parts;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="mb-2 px-4">
        <div className="flex items-start gap-2">
          <span className="text-blue-500 shrink-0 select-none">&gt;</span>
          <span className="text-neutral-100 text-sm leading-relaxed whitespace-pre-wrap">{message.content}</span>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="mb-3 px-4">
      {/* Inline tool call activities */}
      {message.activities && message.activities.length > 0 && (
        <ActivityLog activities={message.activities} live={message.isStreaming} />
      )}

      {/* Message text */}
      {message.content && (
        <div className="text-sm leading-relaxed text-neutral-300 space-y-0.5">
          {message.isStreaming ? (
            // During streaming: simple pre-formatted text + blinking cursor
            <span className="whitespace-pre-wrap">
              {message.content}
              <span className="tui-cursor" />
            </span>
          ) : (
            // Completed: full markdown rendering
            renderContent(message.content)
          )}
        </div>
      )}

      {/* Blinking cursor when streaming but no text content yet */}
      {message.isStreaming && !message.content && (
        <span className="tui-cursor text-neutral-500" />
      )}
    </div>
  );
};

export default ChatMessage;
